/**
 * Parse habit tracker data from metafield value
 * Converts completedWorkouts array to workoutDays object for Calendar compatibility
 * Recalculates current streak based on today's date (in case grace period expired)
 */
export async function parseHabitTrackerData(metafieldValue, skipRecalculation = false) {
    if (!metafieldValue) {
        return {
            completedWorkouts: [],
            workoutDays: {},
            currentStreak: 0,
            personalRecord: 0,
            personalRecordDate: null,
            badges: [],
            totalVideosWatched: 0
        };
    }
    
    try {
        const data = typeof metafieldValue === 'string' 
            ? JSON.parse(metafieldValue) 
            : metafieldValue;
        
        // Convert completedWorkouts array to workoutDays object for Calendar
        const workoutDays = {};
        if (data.completedWorkouts && Array.isArray(data.completedWorkouts)) {
            data.completedWorkouts.forEach(workout => {
                workoutDays[workout.date] = {
                    videoId: workout.videoId,
                    timestamp: workout.timestamp
                };
            });
        }
        
        // Migrate old badge format (array of strings) to new format (array of objects)
        let badges = data.badges || [];
        if (badges.length > 0 && typeof badges[0] === 'string') {
            // Old format - convert to new format with current date as fallback
            badges = badges.map(handle => ({
                handle,
                earnedDate: new Date().toISOString()
            }));
        }
        
        // Recalculate current streak based on today's date
        // The stored value may be stale if the grace period has expired
        // Skip recalculation if explicitly requested (for testing with manual values)
        let currentStreak = data.currentStreak || 0;
        let personalRecord = data.personalRecord || 0;
        
        if (data.completedWorkouts && data.completedWorkouts.length > 0 && !skipRecalculation) {
            try {
                // Import streak calculator
                const { calculateStreaks } = await import('./streakCalculator.js');
                
                // Get most recent workout date
                const sortedDates = data.completedWorkouts
                    .map(w => w.date)
                    .sort((a, b) => new Date(b) - new Date(a));
                const mostRecentDate = sortedDates[0];
                
                // Prepare workout days for calculation
                const workoutDaysForCalc = data.completedWorkouts.map(w => ({ date: w.date }));
                
                // Remove most recent from list, then pass it to calculateStreaks
                const otherWorkouts = workoutDaysForCalc.filter(w => w.date !== mostRecentDate);
                
                // Calculate with proper Sunday handling
                const streakResult = calculateStreaks(otherWorkouts, mostRecentDate, personalRecord);
                currentStreak = streakResult.currentStreak;
                personalRecord = streakResult.personalRecord;
            } catch (err) {
                console.error('Failed to recalculate streak:', err);
                // Fall back to simple check if import fails
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                
                const sortedDates = data.completedWorkouts
                    .map(w => w.date)
                    .sort((a, b) => new Date(b) - new Date(a));
                const lastWorkout = new Date(sortedDates[0]);
                lastWorkout.setHours(0, 0, 0, 0);
                Y
                const daysSinceLastWorkout = Math.floor((today - lastWorkout) / (1000 * 60 * 60 * 24));
                
                if (daysSinceLastWorkout > 1) {
                    currentStreak = 0;
                }
            }
        }
        
        return {
            completedWorkouts: data.completedWorkouts || [],
            workoutDays: workoutDays,
            currentStreak: currentStreak,
            personalRecord: personalRecord,
            personalRecordDate: data.personalRecordDate || null,
            badges: badges,
            totalVideosWatched: data.totalVideosWatched || 0
        };
    } catch (error) {
        console.error('Failed to parse habit tracker data:', error);
        return {
            completedWorkouts: [],
            workoutDays: {},
            currentStreak: 0,
            personalRecord: 0,
            personalRecordDate: null,
            badges: [],
            totalVideosWatched: 0
        };
    }
}

/**
 * Get habit tracker data from Admin API via backend
 */
export async function getHabitTrackerData() {
    console.log('📥 getHabitTrackerData called - fetching from Admin API...');
    
    try {
        // Use Admin API client to fetch data from backend
        const { adminApi } = await import('./adminApiClient.js');
        console.log('📥 Admin API client imported');
        
        await adminApi.init();
        console.log('📥 Admin API client initialized');
        
        const data = await adminApi.getHabitTracker();
        console.log('📥 Data received from Admin API:', data);
        
        // Transform Admin API response to match expected format
        const completedWorkouts = [];
        const workoutDays = data.workoutDays || {};
        
        // Convert workoutDays object to completedWorkouts array
        Object.keys(workoutDays).forEach(date => {
            const workout = workoutDays[date];
            completedWorkouts.push({
                date: date,
                videoId: workout.videoId || null,
                timestamp: workout.completedAt || new Date().toISOString()
            });
        });
        
        // Transform badges to expected format
        const badges = (data.badges || []).map(badge => ({
            handle: badge.handle,
            earnedDate: badge.earnedDate
        }));
        
        const result = {
            completedWorkouts,
            workoutDays,
            currentStreak: data.currentStreak || 0,
            personalRecord: data.personalRecord || 0,
            personalRecordDate: data.personalRecordDate || null,
            badges,
            totalVideosWatched: completedWorkouts.length
        };
        
        console.log('✅ Transformed habit tracker data:', result);
        return result;
    } catch (error) {
        console.error('❌ Failed to get habit tracker data from Admin API:', error);
        // Return default structure on error
        return {
            completedWorkouts: [],
            workoutDays: {},
            currentStreak: 0,
            personalRecord: 0,
            personalRecordDate: null,
            badges: [],
            totalVideosWatched: 0
        };
    }
}

/**
 * Complete a workout - uses Admin API via backend
 */
export async function completeWorkout(date, videoId) {
    try {
        // Use Admin API client to complete workout via backend
        const { adminApi } = await import('./adminApiClient.js');
        await adminApi.init();
        
        console.log('💾 Completing workout via Admin API:', { date, videoId });
        
        const result = await adminApi.completeWorkout(date, videoId);
        
        if (!result.success) {
            throw new Error(result.error || 'Failed to complete workout');
        }
        
        console.log('✅ Workout completed successfully:', result);
        
        // Fetch badge metaobjects if new badges were awarded
        let newlyAwardedBadges = [];
        if (result.newBadges && result.newBadges.length > 0) {
            try {
                const { storefrontClient } = await import('./storefrontClient.js');
                
                const query = `
                    query getBadges {
                        metaobjects(type: "badge", first: 50) {
                            edges {
                                node {
                                    id
                                    handle
                                    fields {
                                        key
                                        value
                                        reference {
                                            ... on MediaImage {
                                                image {
                                                    url
                                                    altText
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                `;
                
                const data = await storefrontClient.graphql(query);
                
                if (data?.metaobjects?.edges) {
                    const badgeMetaobjects = data.metaobjects.edges.map(({ node }) => {
                        const fields = {};
                        node.fields.forEach(field => {
                            if (field.key === 'image' && field.reference?.image) {
                                fields[field.key] = field.reference.image.url;
                            } else {
                                fields[field.key] = field.value;
                            }
                        });
                        
                        return {
                            id: node.id,
                            handle: node.handle,
                            name: fields.name || '',
                            description: fields.description || '',
                            image: fields.image || null
                        };
                    });
                    
                    // Match badges from result with metaobjects
                    newlyAwardedBadges = result.newBadges.map(badge => {
                        const metaobject = badgeMetaobjects.find(b => b.handle === badge.handle);
                        return {
                            handle: badge.handle,
                            name: metaobject?.name || badge.name,
                            description: metaobject?.description || badge.description,
                            image: metaobject?.image || null,
                            fields: {
                                image: metaobject?.image || null,
                                name: metaobject?.name || null,
                                description: metaobject?.description || null
                            }
                        };
                    });
                }
            } catch (err) {
                console.error('🎖️ Error fetching badge metaobjects:', err);
                // Use badges from result as fallback
                newlyAwardedBadges = result.newBadges;
            }
        }
        
        return {
            success: true,
            newBadges: newlyAwardedBadges,
            currentStreak: result.currentStreak,
            personalRecord: result.personalRecord
        };
    } catch (error) {
        console.error('Failed to complete workout via Admin API:', error);
        return {
            success: false,
            error: error.message
        };
    }
}


